import { logger } from '../../../utils/logger';

export type EventTerms = {
  title: string;
  description: string;
};

const EMPTY_TERMS: EventTerms = { title : '', description : '' };

/**
 * Safely parses the event `terms` attribute — a JSON-encoded string in the
 * shape `{"title": string, "description": string}` — into a structured object.
 *
 * Never throws: returns empty strings when the value is missing, malformed
 * (invalid JSON), or lacks the expected string keys. This is the single source
 * of truth for reading terms, shared by the modal trigger guard and the modal
 * component itself.
 *
 * @param rawValue - The raw `terms.value` string coming from the event API.
 * @returns The parsed title and description (empty strings when absent).
 */
export const parseEventTerms = (rawValue?: string | null): EventTerms => {
  if (!rawValue) return { ...EMPTY_TERMS };

  try {
    const parsed = JSON.parse(rawValue);
    return {
      title : typeof parsed?.title === 'string' ? parsed.title : '',
      description : typeof parsed?.description === 'string' ? parsed.description : '',
    };
  } catch {
    logger.warn('[eventTerms] Failed to parse event terms JSON; falling back to empty terms.');
    return { ...EMPTY_TERMS };
  }
};

/**
 * Determines whether the terms modal has content worth showing.
 *
 * The modal must open only when there is actual content — a title and/or a
 * description. An event configured with empty terms (e.g.
 * `{"title":"","description":""}`) is a truthy string but carries no content,
 * so it must NOT trigger the modal.
 *
 * @param rawValue - The raw `terms.value` string coming from the event API.
 * @returns `true` when title and/or description have non-whitespace content.
 */
export const hasTermModalContent = (rawValue?: string | null): boolean => {
  const { title, description } = parseEventTerms(rawValue);
  return Boolean(title.trim() || description.trim());
};
